'use client'

import Image from 'next/image'
import { useParams } from 'next/navigation'
import { Image as DatoImage } from 'react-datocms'
import { formatDate } from '@/utils/date'
import { cn } from '@desoutter/uikit'
import { ArticleRecord } from '@/graphql/types'
import { Button, Calendar, Heading, RightTopArrow } from '@desoutter/uikit'
import { LocalizedLink } from '@/components/LocalizedLink'

type ArticlePreviewProps = {
	data: ArticleRecord
	hideDate?: boolean
	isLocked?: boolean
}

export function ArticlePreview({
	data,
	hideDate = false,
	isLocked = false,
}: ArticlePreviewProps) {
	const { lang } = useParams()

	return (
		<LocalizedLink
			locale={lang}
			prefetch={false}
			href={`/articles/${data.slug}`}
			className={cn(
				'relative flex flex-col mb-5 gap-5 w-full',
				data.span === 2 && 'col-span-2',
				data.span === 3 && 'col-span-3',
				data.fullBackground &&
					'justify-end h-[587px] bg-cover rounded-2xl text-foreground',
			)}
		>
			{data.fullBackground && (
				<DatoImage
					layout="fill"
					objectFit="cover"
					data={data.image.responsiveImage}
					pictureClassName="!transition-all !duration-500 hover:scale-110"
					className="absolute h-full rounded-2xl"
				/>
			)}
			<div className="absolute top-4 left-4 flex flex-row flex-wrap gap-1 z-20">
				{data.industries.map((industry) => (
					<div
						key={industry.id}
						className="bg-foreground text-background rounded px-3 py-1"
					>
						<p className="text-xs">{industry.label}</p>
					</div>
				))}
				{isLocked && (
					<div className="bg-foreground rounded px-3 pt-1.5">
						<Image src="/images/lock.svg" width={12} height={12} alt="lock" />
					</div>
				)}
			</div>

			{!data.fullBackground && data.image?.responsiveImage && (
				<DatoImage
					data={data.image.responsiveImage}
					objectFit="cover"
					layout="responsive"
					objectPosition={`${data.image.focalPoint.x * 100}% ${
						data.image.focalPoint.y * 100
					}%`}
					pictureClassName="!transition-all !duration-500 hover:scale-110"
					className="h-[271px] rounded-2xl"
				/>
			)}

			<div className="absolute top-4 right-4 z-20">
				<Button
					size="xl"
					title="go to article"
					color="tertiary"
					icon={RightTopArrow}
				/>
			</div>

			<div
				className={cn(
					'relative flex flex-col justify-between max-w-[800px] min-h-36',
					data.fullBackground && 'p-6',
				)}
			>
				<div className="z-20">
					<Heading
						as="h3"
						className={cn(
							'h3 tracking-normal font-medium leading-tight dark:text-foreground text-inherit',
							data.fullBackground ? 'text-2xl leading-tight' : 'text-lg',
						)}
					>
						{data.title}
					</Heading>

					<p
						className={`text-sm line-clamp-2 dark:text-foreground text-inherit`}
					>
						{data.description}
					</p>
				</div>
				{!hideDate && data.altDate && data.displayDate && (
					<div className="flex items-center mt-3 gap-2 z-20">
						<Calendar
							variant={data.fullBackground ? 'stroke-white' : 'stroke-gray-600'}
						/>
						<p
							className={cn('text-sm', !data.fullBackground && 'text-gray-600')}
						>
							{formatDate(data.altDate)}
						</p>
					</div>
				)}
			</div>
		</LocalizedLink>
	)
}
